"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Link from "next/link";

const portfolioItems = [
  {
    id: "p1",
    title: "Villa Los Llanos",
    category: "Arquitectura",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_128ba69f8-1772803193876.png",
    alt: "Render arquitectónico de villa moderna con piscina en Las Palmas de Gran Canaria",
    size: "bento-tall",
  },
  {
    id: "p2",
    title: "Escultura Atlántica",
    category: "Arte 3D",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_174ea1487-1772803194062.png",
    alt: "Escultura 3D abstracta inspirada en las olas del Atlántico con formas fluidas",
    size: "",
  },
  {
    id: "p3",
    title: "Prototipo Industrial",
    category: "Ingeniería",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1012f03af-1772803192167.png",
    alt: "Prototipo mecánico impreso en 3D con piezas de precisión en color gris",
    size: "",
  },
  {
    id: "p4",
    title: "Colección Canaria",
    category: "Decoración",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d102965d-1772803203825.png",
    alt: "Colección de figuras decorativas 3D inspiradas en la fauna y flora de Canarias",
    size: "bento-wide",
  },
  {
    id: "p5",
    title: "Joyería Digital",
    category: "Diseño de Producto",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_178e47459-1772803200306.png",
    alt: "Render de joyería diseñada en 3D con acabado dorado y piedras preciosas",
    size: "",
  },
];

export default function PortfolioGrid() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    const items = ref?.current?.querySelectorAll(".reveal, .reveal-scale");
    items?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="portafolio" ref={ref} className="py-28 px-6 lg:px-10 bg-evolvia-bg-light relative">
      {/* Top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-evolvia-copper/20 to-transparent" />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="reveal">
            <span className="text-xs font-700 uppercase tracking-[0.25em] text-evolvia-copper mb-3 block">
              Portafolio
            </span>
            <h2 className="text-5xl md:text-6xl font-black leading-[0.92] tracking-tight text-evolvia-text-dark">
              Trabajo que
              <br />
              <span className="gradient-text-copper">habla por sí solo.</span>
            </h2>
          </div>
          <div className="reveal delay-200 flex items-center gap-4">
            <div className="text-center px-6 py-4 border border-gray-200 rounded-sm">
              <p className="text-3xl font-black gradient-text-copper">120+</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Proyectos</p>
            </div>
            <div className="text-center px-6 py-4 border border-gray-200 rounded-sm">
              <p className="text-3xl font-black text-evolvia-text-dark">7</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Años exp.</p>
            </div>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-[280px]">
          {portfolioItems?.map((item, i) => (
            <div
              key={item?.id}
              className={`reveal-scale delay-${i * 100} relative rounded-sm overflow-hidden group cursor-pointer border border-gray-200 hover:border-evolvia-copper transition-colors duration-400 ${item?.size}`}
            >
              {/* Image */}
              <div className="absolute inset-0 overflow-hidden">
                <AppImage
                  src={item?.image}
                  alt={item?.alt}
                  fill
                  className="product-img object-cover"
                  sizes="(max-width: 768px) 100vw, 33vw"
                />
              </div>

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-evolvia-copper/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="transform translate-y-2 group-hover:translate-y-0 transition-transform duration-400">
                  <span className="text-xs font-700 uppercase tracking-widest mb-2 block text-evolvia-copper">
                    {item?.category}
                  </span>
                  <h3 className="text-xl font-700 text-white">
                    {item?.title}
                  </h3>
                </div>
              </div>

              {/* Arrow */}
              <div className="absolute top-4 right-4 w-8 h-8 rounded-sm opacity-0 group-hover:opacity-100 transition-all duration-400 flex items-center justify-center bg-evolvia-copper">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7H7M17 7v10" />
                </svg>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12 reveal delay-500">
          <Link href="/products" className="btn-outline-dark">
            Ver Portafolio
          </Link>
        </div>
      </div>
    </section>
  );
}